import React, { useState, useCallback }       from 'react';
import PropTypes                              from 'prop-types';
import { Modal, Header, Form, Button, Table } from 'semantic-ui-react';
import omit                                   from 'lodash/omit';

import uniqueId                     from 'utils/uniqueId';
import { indexOnAttributeWithMap }  from 'utils/indexOnAttribute';

import DataGroupField from './DataGroupField';

// -----------------------------------------------------
// Helper
// -----------------------------------------------------

const newRecordIdPrefix = 'new';
const initialChanges    = [];
const initialDeletions  = [];

// merges changes with data groups, while preserving insertion order.
function mergeChanges(dataGroupRecords, changes, deletions) {
  const indexedRecords  = indexOnAttributeWithMap(dataGroupRecords, 'id');
  const indexedChanges  = indexOnAttributeWithMap(changes, 'id');
  const merged          = new Map([ ...indexedRecords, ...indexedChanges ]);

  deletions.forEach(id => merged.delete(id));

  return [ ...merged.values() ];
}

// -----------------------------------------------------
// Component Definition
// -----------------------------------------------------

const DataGroupRecordsModal = ({ opened, saving, onClose, onSave, loading, dataGroupRecords }) => {
  const [changes, setChanges]       = useState(initialChanges);
  const [deletions, setDeletions]   = useState(initialDeletions);

  const dataGroupRecordsWithChanges = mergeChanges(dataGroupRecords, changes, deletions);
  const selectedDataGroupIds        = dataGroupRecordsWithChanges
                                      .map(({ data_group_id }) => data_group_id);

  const formId = uniqueId();

  // -----------------------------------------------------
  // Callbacks
  // -----------------------------------------------------

  const handleCancel = useCallback(() => {
    setChanges(initialChanges);
    setDeletions(initialDeletions);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    const records = dataGroupRecordsWithChanges.map(record => {
      const { id } = record;

      if(id.startsWith && id.startsWith(newRecordIdPrefix)) {
        return omit(record, 'id');
      }

      return record;
    });

    onSave(records, () => handleCancel());
  }, [dataGroupRecordsWithChanges, onSave, handleCancel]);

  const handleAdd = (evt) => {
    evt.preventDefault();
    setChanges([...changes, { id: `${newRecordIdPrefix}.${uniqueId()}` }]);
  };

  const handleChange = (dataGroupRecord) => {
    setChanges([...changes, dataGroupRecord]);
  };

  const handleRemove = (id) => {
    setDeletions([...deletions, id]);
  };

  return (
    <Modal open={ opened } onClose={ onClose } dimmer='inverted' closeIcon>
      <Header icon='folder open' content='Manage Data Groups' />

      <Modal.Content>
        <h4>Add or delete this record to or from a data group.</h4>

        <Form onSubmit={ handleSubmit } loading={ loading } id={ formId }>
          <Table basic='very'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Data Group</Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>
                  <Button icon='add'
                          size='mini'
                          basic
                          color='blue'
                          onClick={ handleAdd } />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {
                dataGroupRecordsWithChanges.map(({ id, data_group_id }) => (
                  <DataGroupField key={ id }
                                  id={ id }
                                  dataGroupId={ data_group_id }
                                  selectedDataGroupIds={ selectedDataGroupIds }
                                  onChange={ handleChange }
                                  onRemove={ handleRemove } />
                ))
              }
            </Table.Body>
          </Table>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={ handleCancel } negative basic>Cancel</Button>
        <Button type='submit'
                loading={ saving }
                form={ formId }
                positive
                basic>Save</Button>
      </Modal.Actions>
    </Modal>
  );
};

// -----------------------------------------------------
// PropTypes
// -----------------------------------------------------

DataGroupRecordsModal.defaultProps = {
  opened:   false,
  saving:   false,
  loading:  false
};

DataGroupRecordsModal.propTypes = {
  opened:           PropTypes.bool,
  saving:           PropTypes.bool,
  loading:          PropTypes.bool,
  onClose:          PropTypes.func.isRequired,
  onSave:           PropTypes.func.isRequired,
  dataGroupRecords: PropTypes.array.isRequired
};

// -----------------------------------------------------
// Exports
// -----------------------------------------------------

export default DataGroupRecordsModal;
